import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle, Text } from '@careflux/primitives';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { BucketSelect } from './BucketSelect';
import type { TrendChartProps, TrendPointBase, TrendSeries } from './components.types';
import { formatDay } from './formatting';
import { chartFrameClasses, emptyStateClasses } from './summaryQuality.styles';

const toChartConfig = <Point extends TrendPointBase>(series: TrendSeries<Point>[]): ChartConfig =>
  Object.fromEntries(
    series.map((entry) => [
      entry.dataKey,
      entry.darkColor
        ? { label: entry.label, theme: { light: entry.color, dark: entry.darkColor } }
        : { label: entry.label, color: entry.color },
    ])
  );

const lastDefinedIndex = <Point extends TrendPointBase>(points: Point[], dataKey: keyof Point) =>
  points.reduce((last, point, index) => (point[dataKey] != null ? index : last), -1);

/** Direct label at a line's last point, so identity isn't carried by color alone. */
const endLabel =
  (text: string, lastIndex: number) =>
  ({ x, y, index }: { x?: number | string; y?: number | string; index?: number }) =>
    index === lastIndex ? (
      <text x={Number(x) + 6} y={Number(y)} dy={4} className="fill-muted-foreground text-xs">
        {text}
      </text>
    ) : (
      <g />
    );

const shortDate = (value: string) => new Date(value).toLocaleDateString('is-IS', { month: 'short', day: 'numeric' });

export const TrendChart = <Point extends TrendPointBase>({
  title,
  description,
  points,
  series,
  formatValue,
  yAxisDomain,
  bucket,
  onBucketChange,
  footnote,
}: TrendChartProps<Point>) => {
  const hasValues = points.some((point) => series.some((entry) => point[entry.dataKey] != null));

  return (
    <Card padding="lg">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
        <CardAction>
          <BucketSelect value={bucket} onChange={onBucketChange} />
        </CardAction>
      </CardHeader>
      <CardContent>
        {hasValues ? (
          <ChartContainer config={toChartConfig(series)} className={chartFrameClasses}>
            <LineChart data={points} margin={{ top: 8, right: 48, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={shortDate} />
              <YAxis
                domain={yAxisDomain}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={64}
                tickFormatter={(value) => formatValue(Number(value))}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(label) => formatDay(String(label))}
                    valueFormatter={(value) => formatValue(Number(value))}
                  />
                }
              />
              {series.length > 1 && <ChartLegend content={<ChartLegendContent />} />}
              {series.map((entry) => (
                <Line
                  key={entry.dataKey}
                  type="monotone"
                  dataKey={entry.dataKey}
                  name={entry.dataKey}
                  stroke={`var(--color-${entry.dataKey})`}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 4 }}
                  label={series.length > 1 ? endLabel(entry.label, lastDefinedIndex(points, entry.dataKey)) : false}
                />
              ))}
            </LineChart>
          </ChartContainer>
        ) : (
          <div className={emptyStateClasses}>Engin gögn fyrir valið tímabil.</div>
        )}
        {footnote ? (
          <Text asChild size="xs" color="muted">
            <p className="mt-2">{footnote}</p>
          </Text>
        ) : null}
      </CardContent>
    </Card>
  );
};
